import React, { createContext, useContext, useRef, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CheckCircle, X } from 'lucide-react';
import { BASE_URL } from "../config/apiconfig";
import { useNavigate } from "react-router-dom";

const PaymentGatewayContext = createContext();
export const usePaymentGateway = () => useContext(PaymentGatewayContext);

export const PaymentGatewayProvider = ({ children }) => {
  // Safe navigation hook that handles Router context availability
  const getSafeNavigate = useCallback(() => {
    try {
      // Try to get navigate function from React Router
      const navigate = useNavigate();
      return navigate;
    } catch (error) {
      // If useNavigate fails (no Router context), return fallback function
      console.warn("useNavigate not available, using fallback navigation:", error.message);
      return (path, options = {}) => {
        if (options.replace) {
          window.location.replace(path);
        } else {
          window.location.href = path;
        }
      };
    }
  }, []);

  // Get auth functions safely
  const getAuthFunctions = () => {
    try {
      // Try to get user and logout from localStorage and context
      const user = JSON.parse(localStorage.getItem("user") || "null");

      // Simple logout function that clears localStorage
      const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.dispatchEvent(new Event("auth-change"));
      };

      return { user, logout };
    } catch (error) {
      console.error("Error getting auth functions:", error);
      return {
        user: null,
        logout: () => {
          localStorage.clear();
          window.location.href = "/signin";
        }
      };
    }
  };

  // Single instance tracking to prevent multiple payments
  const activeInstanceRef = useRef(null);
  const isProcessingRef = useRef(false);
  const pendingOrdersRef = useRef(new Map());
  const modalSessionActiveRef = useRef(false); // Track if modal session is active
  const currentCallbacksRef = useRef(null); // Store current callbacks

  // Safe navigation function
  const safeNavigate = useCallback((path, options = {}) => {
    try {
      const navigate = getSafeNavigate();
      navigate(path, options);
    } catch (error) {
      console.error("Navigation error:", error);
      // Fallback to window.location
      if (options.replace) {
        window.location.replace(path);
      } else {
        window.location.href = path;
      }
    }
  }, [getSafeNavigate]);

  // Check user account status before payment
  const checkUserStatus = useCallback(async (userId) => {
    const { logout } = getAuthFunctions();

    try {
      console.log(`Checking user status for user ID: ${userId}`);

      const response = await axios.get(`${BASE_URL}/user_status/${userId}`, {
        timeout: 10000, // 10 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('User status response:', response.data);

      if (response.data.status === 200 && response.data.data) {
        const { account_status } = response.data.data;

        if (account_status === 'blocked') {
          console.log('User account is blocked, logging out...');

          // Show error message
          toast.error('Your account has been blocked. Please contact support for assistance.', {
            duration: 6000,
            icon: <X size={16} className="text-red-600" />,
          });

          // Logout user and redirect to login
          logout();
          safeNavigate('/signin', { replace: true });

          return {
            success: false,
            blocked: true,
            message: 'Account is blocked'
          };
        } else if (account_status === 'active') {
          console.log('User account is active, proceeding with payment...');
          return {
            success: true,
            active: true,
            message: 'Account is active'
          };
        } else {
          console.log(`Unknown account status: ${account_status}`);
          toast.error('Unable to verify account status. Please try again.', {
            duration: 5000,
          });
          return {
            success: false,
            message: `Unknown account status: ${account_status}`
          };
        }
      } else {
        console.error('Invalid response format from user status API');
        toast.error('Unable to verify account status. Please try again.', {
          duration: 5000,
        });
        return {
          success: false,
          message: 'Invalid response format'
        };
      }
    } catch (error) {
      console.error('Error checking user status:', error);

      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error('Request timeout. Please check your connection and try again.', {
          duration: 5000,
        });
        return {
          success: false,
          message: 'Request timeout'
        };
      } else if (error.response?.status === 404) {
        console.log('User not found, proceeding with caution...');
        toast.warning('Unable to verify account status. Proceeding with payment.', {
          duration: 4000,
        });
        // Allow payment to proceed if user not found (backward compatibility)
        return {
          success: true,
          message: 'User not found, allowing payment'
        };
      } else if (error.response?.status === 401) {
        console.log('Unauthorized access, logging out...');
        toast.error('Session expired. Please login again.', {
          duration: 5000,
        });
        logout();
        safeNavigate('/signin', { replace: true });
        return {
          success: false,
          unauthorized: true,
          message: 'Unauthorized access'
        };
      } else {
        // For other errors, show warning but allow payment (to avoid blocking legitimate users)
        console.error('Network or server error during status check:', error.message);
        toast.warning('Unable to verify account status. Proceeding with payment.', {
          duration: 4000,
        });
        return {
          success: true,
          message: 'Status check failed, allowing payment'
        };
      }
    }
  }, [safeNavigate]);

  // Cleanup function to properly destroy Razorpay instances
  const cleanup = useCallback(() => {
    if (activeInstanceRef.current) {
      try {
        // Remove event listeners
        if (activeInstanceRef.current.off) {
          activeInstanceRef.current.off("payment.failed");
        }

        // Close modal if open
        if (activeInstanceRef.current.close) {
          activeInstanceRef.current.close();
        }
      } catch (error) {
        console.warn("Error during Razorpay cleanup:", error);
      }

      activeInstanceRef.current = null;
    }

    isProcessingRef.current = false;
    modalSessionActiveRef.current = false;
    currentCallbacksRef.current = null;
  }, []);

  // Check if payment is currently active
  const isPaymentActive = useCallback(() => {
    return isProcessingRef.current || activeInstanceRef.current !== null;
  }, []);

  // Force cleanup function for external use
  const forceCleanup = useCallback(() => {
    cleanup();
    pendingOrdersRef.current.clear();
  }, [cleanup]);

  const openRazorpay = useCallback(async (amount, user, callbacks = {}) => {
    // Prevent multiple simultaneous payments
    if (isProcessingRef.current) {
      console.warn("Payment already in progress");
      throw new Error("Payment already in progress");
    }

    // Clean up any existing instances
    cleanup();

    // Mark as processing and modal session as active
    isProcessingRef.current = true;
    modalSessionActiveRef.current = true;

    // Store callbacks for this session
    currentCallbacksRef.current = callbacks;

    try {
      // Check user status before proceeding with payment
      if (user?.id) {
        console.log('=== CHECKING USER STATUS BEFORE PAYMENT ===');
        const statusCheck = await checkUserStatus(user.id);

        if (!statusCheck.success) {
          // If user is blocked or unauthorized, cleanup and return
          cleanup();

          if (statusCheck.blocked) {
            throw new Error("Account is blocked. Payment cannot be processed.");
          } else if (statusCheck.unauthorized) {
            throw new Error("Session expired. Please login again.");
          } else {
            // For other failures, we already allowed payment to proceed in checkUserStatus
            // So we continue here only if the function returned success: false for blocking reasons
            throw new Error(statusCheck.message || "Unable to verify account status");
          }
        }

        console.log('✅ User status check passed, proceeding with payment...');
      } else {
        console.warn('No user ID provided, skipping status check');
        toast.warning('Unable to verify user status. Proceeding with payment.', {
          duration: 4000,
        });
      }

      // Create unique order identifier for deduplication
      const orderKey = `${amount}_${user?.id || 'guest'}_${Date.now()}`;

      // Check for duplicate order creation
      if (pendingOrdersRef.current.has(orderKey)) {
        throw new Error("Duplicate order creation attempt");
      }

      // Mark order as pending
      pendingOrdersRef.current.set(orderKey, true);

      console.log("Creating payment order for amount:", amount);

      // Create order from backend with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      let res;
      try {
        res = await axios.post(
          `${BASE_URL}/order`,
          { amount },
          {
            signal: controller.signal,
            timeout: 15000,
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
      } finally {
        clearTimeout(timeoutId);
        pendingOrdersRef.current.delete(orderKey);
      }

      if (res.data.status !== "success") {
        throw new Error(res.data.message || "Failed to create payment order");
      }

      const { order_id, amount: orderAmount, currency } = res.data;
      console.log("Payment order created successfully:", { order_id, orderAmount });

      // Return promise for Razorpay modal
      return new Promise((resolve, reject) => {
        // Double-check if still processing (user might have cancelled)
        if (!isProcessingRef.current) {
          reject({
            success: false,
            cancelled: true,
            reason: "payment_cancelled_before_modal",
            message: "Payment was cancelled"
          });
          return;
        }

        const options = {
          key: "rzp_test_SSeEqDbAczl7iq",
          amount: orderAmount,
          currency,
          name: "Manaprint",
          description: "Order Payment",
          order_id,
          prefill: {
            name: user?.name || user?.first_name || "",
            email: user?.email || "",
            contact: user?.phone || "",
          },
          theme: { color: "#008080" },
          modal: {
            escape: true,
            ondismiss: function() {
              console.log("Razorpay modal dismissed by user");

              // Only show toast if this is still the active payment
              if (modalSessionActiveRef.current) {
                toast.error("Payment cancelled. You can retry anytime.", {
                  duration: 4000,
                  icon: <X size={16} className="text-red-600" />,
                });
              }

              // Call custom callback before cleanup
              if (currentCallbacksRef.current?.onModalClose) {
                try {
                  currentCallbacksRef.current.onModalClose();
                } catch (error) {
                  console.error('Error in onModalClose callback:', error);
                }
              }

              // Cleanup when modal is actually dismissed
              cleanup();

              reject({
                success: false,
                cancelled: true,
                reason: "modal_dismissed",
                message: "Payment cancelled by user"
              });
            }
          },
          method: {
            card: true,
            netbanking: true,
            upi: true,
            wallet: true,
            emi: false, // Disable EMI to reduce resource loading
          },
          handler: function(response) {
            console.log("Payment successful:", response);

            // Check if modal session is still active
            if (!modalSessionActiveRef.current) {
              console.warn("Payment success for inactive modal session, ignoring");
              return;
            }

            // Check if we have valid callbacks
            if (!currentCallbacksRef.current) {
              console.warn("No callbacks available for payment success");
              return;
            }

            toast.success("Payment Successful!", { icon: <CheckCircle size={16} className="text-green-600" /> });

            // Mark session as completed but don't cleanup yet (let ondismiss handle it)
            modalSessionActiveRef.current = false;

            // Call custom success callback
            if (currentCallbacksRef.current.onSuccess) {
              try {
                // Use requestAnimationFrame for better performance
                requestAnimationFrame(() => {
                  currentCallbacksRef.current.onSuccess(response);
                });
              } catch (error) {
                console.error('Error in onSuccess callback:', error);
              }
            }

            resolve({
              success: true,
              paymentResponse: response,
              orderId: order_id
            });
          }
        };

        try {
          // Create Razorpay instance
          const rzp = new window.Razorpay(options);
          activeInstanceRef.current = rzp;

          // Payment failure listener - CRITICAL: Don't interfere with retry
          const failureHandler = function(response) {
            console.error("Payment failed:", response);

            // Only show error if modal session is still active
            if (modalSessionActiveRef.current) {
              toast.error("Payment failed. Please try again.", {
                duration: 5000,
                icon: <X size={16} className="text-red-600" />,
              });
            }

            // Call custom failure callback but don't cleanup - allow retry
            if (currentCallbacksRef.current?.onFailure) {
              try {
                currentCallbacksRef.current.onFailure(response);
              } catch (error) {
                console.error('Error in onFailure callback:', error);
              }
            }

            // CRITICAL: Don't reject the promise here - let user retry
            // The modal stays open for retry, only close if user dismisses manually
            console.log("Payment failed, but keeping modal session active for retry");
          };

          // Add failure listener
          rzp.on("payment.failed", failureHandler);

          // Open the Razorpay modal
          rzp.open();

        } catch (error) {
          console.error("Error creating Razorpay instance:", error);
          cleanup();
          reject({
            success: false,
            reason: "razorpay_instance_error",
            message: error.message || "Failed to create payment interface"
          });
        }
      });

    } catch (error) {
      console.error("Payment initiation error:", error);

      // Cleanup on error
      cleanup();

      // Handle specific error types
      if (error.name === 'AbortError') {
        toast.error("Payment request timed out. Please try again.");
        throw new Error("Payment request timed out");
      } else if (error.message === "Payment already in progress") {
        toast.warning("Payment is already in progress. Please wait.");
        throw error;
      } else if (error.message === "Duplicate order creation attempt") {
        toast.warning("Order is being created. Please wait.");
        throw error;
      } else if (error.message.includes("Account is blocked")) {
        // Don't show additional toast as it's already shown in checkUserStatus
        throw error;
      } else if (error.message.includes("Session expired")) {
        // Don't show additional toast as it's already shown in checkUserStatus
        throw error;
      } else {
        toast.error("Payment initiation failed. Please try again.");
        throw error;
      }
    }
  }, [cleanup, checkUserStatus]);

  const createPaymentOrder = useCallback(async (amount, user, callbacks) => {
    return openRazorpay(amount, user, callbacks);
  }, [openRazorpay]);

  const contextValue = {
    openRazorpay,
    createPaymentOrder,
    forceCleanup,
    isPaymentActive,
    checkUserStatus
  };

  return (
    <PaymentGatewayContext.Provider value={contextValue}>
      {children}
    </PaymentGatewayContext.Provider>
  );
};
